import { globalWebSocketManager } from "@/features/shot-tracking/services/global-websocket-manager";
import type { WebSocketEvent } from "@/services/connectivity/websocket-client/websocket-client";
import { networkMonitor } from "@/services/monitoring/network-monitor/network-monitor";
import type { NetworkStatus } from "@/services/monitoring/network-monitor/network-monitor";

import {
  applyWebSocketEvent,
  createConnectionStateMap,
  type ConnectionStateMap,
} from "./connection-state";

type NetworkDebugEntry = {
  timestamp: number;
  type: "websocket" | "network" | "connection" | "http";
  event: string;
  data?: unknown;
  status?: string;
  details?: Record<string, unknown>;
  radarId?: string;
};

type HttpRequestOutcome = "sent" | "queued" | "rejected" | "failed";

type HttpRequestLog = {
  service: string;
  method: string;
  url: string;
  status?: number;
  durationMs: number;
  error?: string;
  requestBody?: string;
  /** Human-readable body, or a note (e.g. "binary, 12.3 KB") for non-text responses. */
  responseBody?: string;
  /** Terminal outcome for calls that can be queued for later retry (e.g. claim mutations). */
  outcome?: HttpRequestOutcome;
  radarId?: string;
};

type NetworkDebugConfig = {
  maxEntries?: number;
};

class NetworkDebugMonitor {
  private entries: NetworkDebugEntry[] = [];
  private maxEntries: number;
  private listeners: Array<(entries: NetworkDebugEntry[]) => void> = [];
  private isMonitoring = false;
  private wsUnsubscribe: (() => void) | null = null;
  private networkUnsubscribe: (() => void) | null = null;

  private connectionStateByRadarId: ConnectionStateMap =
    createConnectionStateMap();

  private networkState = {
    isOnline: navigator.onLine,
    connectionType: "unknown",
    effectiveType: "unknown",
    downlink: 0,
    rtt: 0,
  };

  constructor(config: NetworkDebugConfig = {}) {
    this.maxEntries = config.maxEntries || 500;
  }

  public start(): void {
    if (this.isMonitoring) {
      return;
    }

    this.isMonitoring = true;

    this.addEntry({
      timestamp: Date.now(),
      type: "connection",
      event: "monitoring_started",
      status: "info",
    });

    this.wsUnsubscribe = globalWebSocketManager.addRawListener(
      (radarId, event) => {
        this.handleWebSocketEvent(radarId, event);
      }
    );

    this.networkUnsubscribe = networkMonitor.addListener((event) => {
      this.handleNetworkEvent(event);
    });

    const initialNetworkStatus = networkMonitor.getNetworkStatus();
    this.updateNetworkState(initialNetworkStatus);
  }

  public stop(): void {
    if (!this.isMonitoring) {
      return;
    }

    this.isMonitoring = false;

    if (this.wsUnsubscribe) {
      this.wsUnsubscribe();
      this.wsUnsubscribe = null;
    }

    if (this.networkUnsubscribe) {
      this.networkUnsubscribe();
      this.networkUnsubscribe = null;
    }

    this.addEntry({
      timestamp: Date.now(),
      type: "connection",
      event: "monitoring_stopped",
      status: "info",
    });
  }

  private handleWebSocketEvent(radarId: string, event: WebSocketEvent): void {
    if (!this.isMonitoring) {
      return;
    }

    const entry: NetworkDebugEntry = {
      timestamp: Date.now(),
      type: "websocket",
      event: event.type,
      radarId,
    };

    this.connectionStateByRadarId = applyWebSocketEvent(
      this.connectionStateByRadarId,
      radarId,
      event
    );

    switch (event.type) {
      case "connected":
        entry.status = "success";
        entry.details = {
          connectedAt: new Date().toISOString(),
        };
        break;

      case "disconnected":
        entry.status = "warning";
        entry.details = {
          disconnectedAt: new Date().toISOString(),
        };
        break;

      case "message": {
        entry.status = "info";
        entry.data = event.message;

        const raw = event.message as Record<string, unknown> | undefined;
        const messageType = raw?.Type ?? raw?.type;
        const messageSubType = raw?.SubType ?? raw?.subType;
        const payload = raw?.Payload ?? raw?.data;
        const bayName =
          payload !== null && typeof payload === "object"
            ? (payload as Record<string, unknown>).BayName
            : undefined;

        entry.event = typeof messageType === "string" ? messageType : "message";
        entry.details = {
          ...(messageSubType !== undefined && { subType: messageSubType }),
          ...(typeof bayName === "string" && { bayName }),
          ...(payload !== null &&
            payload !== undefined && {
              payloadKeys:
                typeof payload === "object"
                  ? Object.keys(payload as Record<string, unknown>)
                  : typeof payload,
            }),
        };
        break;
      }

      case "error":
        entry.status = "error";
        entry.data = event.error?.message || "Unknown error";
        break;
    }

    this.addEntry(entry);
  }

  /**
   * Logs a radar REST call (consent, snapshot, system-config, etc).
   * No-op when the debug console is closed. Callers still construct the
   * `log` argument before this check — guard expensive body parsing /
   * stringifying with `networkDebugMonitor.isActive()` first.
   */
  public logHttpRequest(log: HttpRequestLog): void {
    if (!this.isMonitoring) {
      return;
    }

    const outcomeStatus: Record<
      HttpRequestOutcome,
      NetworkDebugEntry["status"]
    > = {
      sent: "success",
      queued: "warning",
      rejected: "error",
      failed: "error",
    };

    let status: NetworkDebugEntry["status"];
    if (log.outcome) {
      status = outcomeStatus[log.outcome];
    } else {
      const isError = log.error !== undefined || (log.status ?? 200) >= 400;
      status = isError ? "error" : "success";
    }

    this.addEntry({
      timestamp: Date.now(),
      type: "http",
      event: `${log.method} ${log.service}`,
      status,
      radarId: log.radarId,
      details: {
        url: log.url,
        method: log.method,
        status: log.status,
        durationMs: log.durationMs,
        error: log.error,
        requestBody: log.requestBody,
        responseBody: log.responseBody,
        outcome: log.outcome,
      },
    });
  }

  private handleNetworkEvent(event: {
    type: string;
    status: NetworkStatus;
  }): void {
    if (!this.isMonitoring) {
      return;
    }

    this.updateNetworkState(event.status);

    let status: NetworkDebugEntry["status"];
    if (event.type === "online") {
      status = "success";
    } else if (event.type === "offline") {
      status = "error";
    } else {
      status = "info";
    }

    const entry: NetworkDebugEntry = {
      timestamp: Date.now(),
      type: "network",
      event: event.type,
      status,
      details: {
        isOnline: event.status.isOnline,
        connectionType: event.status.connectionType,
        effectiveType: event.status.effectiveType,
        downlink: event.status.downlink,
        rtt: event.status.rtt,
      },
    };

    this.addEntry(entry);
  }

  private updateNetworkState(status: NetworkStatus): void {
    this.networkState.isOnline = status.isOnline;
    this.networkState.connectionType = status.connectionType || "unknown";
    this.networkState.effectiveType = status.effectiveType || "unknown";
    this.networkState.downlink = status.downlink || 0;
    this.networkState.rtt = status.rtt || 0;
  }

  private addEntry(entry: NetworkDebugEntry): void {
    if (!this.isMonitoring) {
      return;
    }

    this.entries.push(entry);

    if (this.entries.length > this.maxEntries) {
      this.entries.shift();
    }

    this.notifyListeners();
  }

  private notifyListeners(): void {
    const entriesCopy = [...this.entries];
    this.listeners.forEach((listener) => listener(entriesCopy));
  }

  public getEntries(): NetworkDebugEntry[] {
    return [...this.entries];
  }

  public getConnectionStateByRadarId(): ConnectionStateMap {
    return { ...this.connectionStateByRadarId };
  }

  public getNetworkState() {
    return { ...this.networkState };
  }

  public clearEntries(): void {
    this.entries = [];
    this.notifyListeners();
  }

  public addListener(
    listener: (entries: NetworkDebugEntry[]) => void
  ): () => void {
    this.listeners.push(listener);

    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  public isActive(): boolean {
    return this.isMonitoring;
  }

  public formatEntry(entry: NetworkDebugEntry): string {
    const time = new Date(entry.timestamp).toLocaleTimeString();
    const type = entry.type.toUpperCase();
    const event = entry.event;
    let details = "";
    if (entry.details) {
      details = JSON.stringify(entry.details);
    } else if (entry.data) {
      details = JSON.stringify(entry.data);
    }

    return `[${time}] ${type} - ${event} ${details}`;
  }
}

export const networkDebugMonitor = new NetworkDebugMonitor();
export type { NetworkDebugEntry, HttpRequestOutcome };
export type { RadarConnectionState } from "./connection-state";
